<script setup>
import useVuelidate from '@vuelidate/core'
import { helpers, sameAs } from '@vuelidate/validators'
import { computed, reactive } from 'vue'

import InputCheckbox from '@/components/form/InputCheckbox.vue'
import FunnelStep from '@/components/FunnelStep.vue'
import { useFormDataStore } from '@/stores/FormDataStore'
import { useGiftStore } from '@/stores/GiftStore'
import { usePaymentGatewaysStore } from '@/stores/PaymentGatewaysStore'
import { useSettingsStore } from '@/stores/SettingsStore'
import { useSubscriptionsStore } from '@/stores/SubscriptionsStore'
import { useVoucherStore } from '@/stores/VoucherStore'
import { useHelpers } from '@/useHelpers'

const subscriptionStore = useSubscriptionsStore()
const paymentGatewayStore = usePaymentGatewaysStore()
const voucherStore = useVoucherStore()
const dataStore = useFormDataStore()
const giftStore = useGiftStore()

const { formatMoney } = useHelpers()

const settingsStore = useSettingsStore()

const handleSubmitForm = () => {
  $v.value.$touch()
  if (!$v.value.$invalid) {
    state.isLoading = true
    if (window.init.funnelUrlKey === 'gift' && settingsStore.getVersion === 'CZ') {
      giftStore.submit().then(() => {
        console.log('Gift submitted')
      })
    } else {
      dataStore.submitForm()
    }
  }
}

defineProps({
  id: {
    type: Number,
    required: true
  },
  isActive: {
    type: Boolean,
    required: true
  },
  isCompleted: {
    type: Boolean,
    required: true
  }
})

const state = reactive({
  isLoading: false,
  form: {
    termsAndConditions: false,
    termsRecurringPayments: false,
    termsPaymentInfoStorage: false
  }
})

const paymentCode = computed(
  () => paymentGatewayStore.getGatewayByCode(dataStore.formData.paymentGatewayCode)
)

const isTermsRecurringPaymentsVisible = computed(() => paymentCode.value?.is_recurrent)

const isTermsPaymentInfoStorageVisible = computed(
() => paymentGatewayStore.isGoPayGateway(paymentCode.value?.code)
)

const validationRules = computed(() => {
  let rules = {
    termsAndConditions: {
      sameAs: helpers.withMessage('Value is required', sameAs(true))
    }
  }

  if (isTermsRecurringPaymentsVisible.value) {
    rules.termsRecurringPayments = {
      sameAs: helpers.withMessage('Value is required', sameAs(true))
    }
  }

  if (isTermsPaymentInfoStorageVisible.value) {
    rules.termsPaymentInfoStorage = {
      sameAs: helpers.withMessage('Value is required', sameAs(true))
    }
  }

  return rules
})

const totalPrice = computed(() => {
  if (subscriptionStore.amount) {
    return subscriptionStore.subscriptionPriceForAmount(
      dataStore.selectedSubscriptionWithVariant.code,
      voucherStore.voucher
    )
  }
  return subscriptionStore.subscriptionPrice(
    dataStore.selectedSubscriptionWithVariant.code,
    voucherStore.voucher
  )
})

const $v = useVuelidate(validationRules, state.form, { $stopPropagation: true })
</script>

<template>
  <FunnelStep
    :id="id"
    name=""
    :details="$t('Summary')"
    :is-active="isActive"
    :is-completed="isCompleted"
  >
    <div class="step-summary__form">
      <InputCheckbox
        v-model="$v.termsAndConditions.$model"
        :validator="$v.termsAndConditions"
        :label="$t('I agree with the terms and conditions of company')"
      />

      <InputCheckbox
        v-if="isTermsRecurringPaymentsVisible"
        v-model="$v.termsRecurringPayments.$model"
        :validator="$v.termsRecurringPayments"
      >
        {{ $t('I agree to the terms of charging recurring payments.') }}
      </InputCheckbox>

      <InputCheckbox
        v-if="isTermsPaymentInfoStorageVisible"
        v-model="$v.termsPaymentInfoStorage.$model"
        :validator="$v.termsPaymentInfoStorage"
        :label="
          $t(
            'I agree to the safe storage of my payment data with the payment gateway provider - GoPay. No worries! GoPay handles your data in accordance with PCI-DSS Level 1, the strictest international data security standard in the financial sector.'
          )
        "
      />

      <div class="input-group button-group">
        <button
          @click="handleSubmitForm"
          :class="{
            'button button--large button--primary step-summary__button': true,
            disabled: state.isLoading
          }"
        >
          {{ $t('I will pay amount', { amount: formatMoney(totalPrice) }) }}
        </button>
      </div>
    </div>
  </FunnelStep>
</template>
